// Whale Tracking Types

export interface WhaleWallet {
  address: string;
  label?: string; // e.g., "Binance Cold Wallet", "Unknown Whale #1"
  category: WalletCategory;
  totalValue: string;
  lastActivity: number;
  isTracked: boolean;
  tags: string[];
}

export type WalletCategory =
  | 'exchange'
  | 'whale'
  | 'institution'
  | 'defi_protocol'
  | 'known_entity'
  | 'unknown';

export interface WhaleTransaction {
  id: string;
  txHash: string;
  timestamp: number;

  // Transaction details
  from: WhaleWallet;
  to: WhaleWallet;
  token: TokenInfo;
  amount: string;
  valueUSD: string;

  // Classification
  type: TransactionType;
  significance: 'low' | 'medium' | 'high' | 'critical';

  // Analysis
  marketImpact?: MarketImpact;
  historicalPattern?: string;
}

export type TransactionType =
  | 'exchange_deposit'    // Moving to exchange (potential sell)
  | 'exchange_withdrawal' // Moving from exchange (accumulation)
  | 'whale_transfer'      // Large transfer between wallets
  | 'accumulation'        // Buying large amounts
  | 'distribution'        // Selling/distributing
  | 'protocol_interaction' // DeFi protocol usage
  | 'unknown';

export interface TokenInfo {
  address: string;
  symbol: string;
  name: string;
  decimals: number;
  price: number;
  logoURI?: string;
}

export interface MarketImpact {
  priceChangePercent: number;
  volumeImpact: string;
  confidence: number;
  prediction: 'bullish' | 'bearish' | 'neutral';
  reasoning: string;
}

export interface WhaleAlert {
  id: string;
  timestamp: number;
  type: AlertType;
  transaction?: WhaleTransaction;
  wallet?: WhaleWallet;
  token: TokenInfo;

  // Alert details
  title: string;
  description: string;
  significance: 'low' | 'medium' | 'high' | 'critical';

  // User interaction
  isRead: boolean;
  isSaved: boolean;
}

export type AlertType =
  | 'large_transfer'
  | 'exchange_flow'
  | 'accumulation_pattern'
  | 'distribution_pattern'
  | 'new_whale_activity'
  | 'tracked_wallet_activity'
  | 'unusual_activity';

export interface WhaleTrackingConfig {
  // Minimum transaction size to track (in USD)
  minTransactionSize: number;

  // Tokens to track
  trackedTokens: string[];

  // Notification preferences
  notifications: {
    email: boolean;
    push: boolean;
    inApp: boolean;
  };

  // Alert filters
  alertTypes: AlertType[];
  significanceThreshold: 'low' | 'medium' | 'high' | 'critical';

  // Custom tracked wallets
  customTrackedWallets: string[];
}

export interface WhaleStats {
  totalAlertsToday: number;
  criticalAlerts: number;
  trackedWallets: number;
  totalVolumeTracked: string;
  topMovingToken: {
    symbol: string;
    volume: string;
    direction: 'in' | 'out';
  };
}

export interface AccumulationTrend {
  token: TokenInfo;
  netFlow: string; // Positive = accumulation, Negative = distribution
  whalesBuying: number;
  whalesSelling: number;
  exchangeNetFlow: string;
  trend: 'strong_accumulation' | 'accumulation' | 'neutral' | 'distribution' | 'strong_distribution';
  confidence: number;
}

// Alert significance thresholds (in USD)
export const SIGNIFICANCE_THRESHOLDS = {
  low: 100000,       // $100K+
  medium: 500000,    // $500K+
  high: 1000000,     // $1M+
  critical: 10000000, // $10M+
};

// Known whale/exchange labels
export const KNOWN_WALLETS: Record<string, { label: string; category: WalletCategory }> = {
  '0x28C6c06298d514Db089934071355E5743bf21d60': { label: 'Binance Hot Wallet', category: 'exchange' },
  '0x21a31Ee1afC51d94C2eFcCAa2092aD1028285549': { label: 'Binance Cold Wallet', category: 'exchange' },
  '0xDFd5293D8e347dFe59E90eFd55b2956a1343963d': { label: 'Coinbase', category: 'exchange' },
  '0x2FAF487A4414Fe77e2327F0bf4AE2a264a776AD2': { label: 'FTX (Alameda)', category: 'exchange' },
  '0x40B38765696e3d5d8d9d834D8AaD4bB6e418E489': { label: 'OKEx', category: 'exchange' },
  '0x0D0707963952f2fBA59dD06f2b425ace40b492Fe': { label: 'Gate.io', category: 'exchange' },
};

export const ALERT_TYPE_LABELS: Record<AlertType, string> = {
  large_transfer: 'Transferencia Grande',
  exchange_flow: 'Flujo Exchange',
  accumulation_pattern: 'Patron de Acumulacion',
  distribution_pattern: 'Patron de Distribucion',
  new_whale_activity: 'Nueva Actividad Whale',
  tracked_wallet_activity: 'Wallet Rastreada',
  unusual_activity: 'Actividad Inusual',
};

export const SIGNIFICANCE_COLORS: Record<string, string> = {
  low: 'text-blue-500 bg-blue-500/20',
  medium: 'text-yellow-500 bg-yellow-500/20',
  high: 'text-orange-500 bg-orange-500/20',
  critical: 'text-red-500 bg-red-500/20',
};
